import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building, MapPin, Users, MessageSquare, Settings, LogOut, ChevronDown, User, Gift, CreditCard, Receipt, Shield, Key, Loader2, Terminal, Bell, BellOff, FileText, Send } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { SuperadminSettingsModal } from './SuperadminSettingsModal';
import { SystemLogsModal } from './SystemLogsModal';
import { usePushNotifications } from '../hooks/usePushNotifications';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ProfileDropdown = ({ user, isOpen, onClose, onLogout, currentLocation }) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleAboVerwaltung = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/addon/stripe-portal`, {
        headers: { Authorization: `Bearer ${token}`, origin: window.location.origin }
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Stripe-Portal konnte nicht geöffnet werden.');
      setPortalLoading(false);
    }
  };

  // Menu items based on role
  const menuItems = user?.role === 'superadmin' 
    ? [
        { to: '/superadmin/customers', icon: Users, label: 'Kunden', color: 'text-purple-600' },
        { to: '/notifications', icon: MessageSquare, label: 'Nachrichten', color: 'text-orange-600' },
      ]
    : (user?.role === 'employee')
    ? [
        { to: '/profil/betriebsvereinbarungen', icon: FileText, label: 'Betriebsvereinbarungen', color: 'text-blue-600' },
        { to: '/notifications', icon: MessageSquare, label: 'Nachrichten', color: 'text-orange-600' },
        { to: '/employee/settings', icon: Settings, label: 'Einstellungen', color: 'text-gray-600' },
      ]
    : (user?.role === 'stationsleitung')
    ? [
        { to: '/profil/betriebsvereinbarungen', icon: FileText, label: 'Betriebsvereinbarungen', color: 'text-blue-600' },
        ...(user?.bv_permission ? [
          { to: '/admin/betriebsvereinbarungen', icon: Send, label: 'BV Verwaltung', color: 'text-indigo-600' },
        ] : []),
        { to: '/notifications', icon: MessageSquare, label: 'Nachrichten', color: 'text-orange-600' },
        { to: '/employee/settings', icon: Settings, label: 'Einstellungen', color: 'text-gray-600' },
      ]
    : [
        // Admin: Mein Profil + operative Einzelpunkte
        { to: '/admin/profil',     icon: User,         label: 'Mein Profil',       color: 'text-primary' },
        { action: handleAboVerwaltung, icon: CreditCard, label: 'Zahlungsmethode', color: 'text-emerald-600' },
        { to: '/admin/personal',   icon: Users,        label: 'Personal',          color: 'text-purple-600' },
        { to: '/notifications',    icon: MessageSquare, label: 'Nachrichten',       color: 'text-orange-600' },
      ];

  const handleSettingsClick = () => {
    setShowSettingsModal(true);
  };

  const handleCloseSettingsModal = () => {
    setShowSettingsModal(false);
  };

  return (
    <>
      {/* Backdrop + Dropdown – nur wenn isOpen */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={onClose} />

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-xl border border-border z-50 overflow-hidden">
        {/* User Info Header */}
        <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm sm:text-base truncate">{user?.name}</p>
              <p className="text-xs text-white/80">
                {user?.role === 'superadmin' ? 'Superadmin' : user?.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
              </p>
              {currentLocation && (
                <div className="flex items-center gap-1 mt-1 text-xs text-white/70">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{currentLocation}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          {menuItems.map((item) => {
            const isAboVerwaltung = item.action === handleAboVerwaltung;
            if (item.action) {
              return (
                <button
                  key={item.label}
                  onClick={() => { onClose(); item.action(); }}
                  disabled={isAboVerwaltung && portalLoading}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left disabled:opacity-60"
                  data-testid="profile-abo-verwaltung-btn"
                >
                  {isAboVerwaltung && portalLoading
                    ? <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 animate-spin" />
                    : <item.icon className={`h-4 w-4 sm:h-5 sm:w-5 ${item.color}`} />
                  }
                  <span className="text-sm text-foreground">{item.label}</span>
                </button>
              );
            }
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-4 py-2.5 transition-colors
                  ${item.isDanger 
                    ? 'hover:bg-red-50 dark:hover:bg-red-950/30' 
                    : 'hover:bg-muted'}
                `}
              >
                <item.icon className={`h-4 w-4 sm:h-5 sm:w-5 ${item.color}`} />
                <span className={`text-sm ${item.isDanger ? 'font-medium' : ''} text-foreground`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          
          {/* Superadmin: Settings Button (opens modal) */}
          {user?.role === 'superadmin' && (
            <>
              <button
                onClick={handleSettingsClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left"
                data-testid="profile-settings-btn"
              >
                <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                <span className="text-sm text-foreground">Einstellungen</span>
              </button>
              <button
                onClick={() => { onClose(); setShowLogsModal(true); }}                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left"
                data-testid="profile-system-logs-btn"
              >
                <Terminal className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                <span className="text-sm text-foreground">System Logs</span>
              </button>
            </>
          )}
        </div>

        {/* Theme Toggle */}
        <div className="px-4 py-2 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-2">Design</p>
          <ThemeToggle />
        </div>

        {/* Push Notifications Toggle */}
        <PushToggle />

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Logout */}
        <button
          onClick={() => {
            onClose();
            onLogout();
          }}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-950 transition-colors text-left"
        >
          <LogOut className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-500" />
          <span className="text-sm text-red-600 dark:text-red-500 font-medium">Abmelden</span>
        </button>
        </div>
        </>
      )}

      {/* Superadmin Settings Modal – immer gemountet (außerhalb isOpen) */}
      {user?.role === 'superadmin' && (
        <SuperadminSettingsModal 
          isOpen={showSettingsModal} 
          onClose={handleCloseSettingsModal}
          user={user}
        />
      )}

      {/* System Logs Modal – immer gemountet (außerhalb isOpen) */}
      {user?.role === 'superadmin' && (
        <SystemLogsModal
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
        />
      )}
    </>
  );
};


// Push-Benachrichtigungen Toggle (innerhalb des Dropdowns)
function PushToggle() {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe } = usePushNotifications();

  if (!isSupported) return null;

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
      toast.success('Push-Benachrichtigungen deaktiviert');
    } else {
      const result = await subscribe();
      if (result.success) {
        toast.success('Push-Benachrichtigungen aktiviert!');
      } else if (result.reason === 'denied') {
        toast.error('Berechtigung abgelehnt – bitte in den Browser-Einstellungen aktivieren');
      } else if (result.reason === 'error') {
        toast.error(`Push-Fehler: ${result.error || 'Bitte Seite neu laden und erneut versuchen'}`);
      }
    }
  };

  return (
    <div className="px-4 py-2 border-t border-border">
      <p className="text-xs font-medium text-muted-foreground mb-2">Benachrichtigungen</p>
      <button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        className="w-full flex items-center justify-between gap-3 py-1.5 text-sm text-foreground hover:text-primary transition-colors disabled:opacity-50"
        data-testid="push-toggle-btn"
      >
        <div className="flex items-center gap-2">
          {isSubscribed
            ? <Bell className="h-4 w-4 text-primary" />
            : <BellOff className="h-4 w-4 text-muted-foreground" />
          }
          <span>{isSubscribed ? 'Push aktiv' : 'Push deaktiviert'}</span>
        </div>
        <div className={`relative w-9 h-5 rounded-full transition-colors ${isSubscribed ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isSubscribed ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
      </button>
      {permission === 'denied' && (
        <p className="text-xs text-muted-foreground mt-1">In Browser-Einstellungen blockiert</p>
      )}
    </div>
  );
}
